import { supabase } from '@/integrations/supabase/client';
import { PaymentRequest } from '@/types/database/payment-requests';
import {
  formatNewPaymentApprovalNotification,
  formatPaymentApprovedNotification,
  formatPaymentRejectedNotification,
  formatPaymentConfirmedNotification
} from './formatters/paymentRequest';
import { getRequesterName } from './formatters/paymentRequest';

/**
 * Check if payment bot is configured
 */
export const isPaymentBotConfigured = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-payment-bot', {
      body: { action: 'test-connection' }
    });
    return data?.success === true;
  } catch {
    return false;
  }
};

/**
 * Get connected approvers for a department
 */
const getConnectedApprovers = async (departmentId: string): Promise<number[]> => {
  try {
    // Get approvers for the department
    const { data: approvers, error: approversError } = await supabase
      .from('department_approvers')
      .select('user_id')
      .eq('department_id', departmentId)
      .eq('is_active', true);

    if (approversError || !approvers || approvers.length === 0) {
      return [];
    }

    const userIds = approvers.map(a => a.user_id);

    // Get connected chat IDs
    const { data: connections, error: connectionsError } = await supabase
      .from('telegram_connections')
      .select('telegram_chat_id')
      .in('user_id', userIds)
      .eq('bot_type', 'payment')
      .eq('is_active', true);

    if (connectionsError || !connections) {
      return [];
    }

    return connections.map(c => c.telegram_chat_id);
  } catch (error) {
    console.error('Error getting connected approvers:', error);
    return [];
  }
};

/**
 * Get connected finance users (users with finance role)
 */
const getConnectedFinanceUsers = async (): Promise<number[]> => {
  try {
    // Get users with finance role
    const { data: financeUsers, error: usersError } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('role', 'finance');

    if (usersError || !financeUsers || financeUsers.length === 0) {
      return [];
    }

    const userIds = financeUsers.map(u => u.id);

    // Get connected chat IDs
    const { data: connections, error: connectionsError } = await supabase
      .from('telegram_connections')
      .select('telegram_chat_id')
      .in('user_id', userIds)
      .eq('bot_type', 'payment')
      .eq('is_active', true);

    if (connectionsError || !connections) {
      return [];
    }

    return connections.map(c => c.telegram_chat_id);
  } catch (error) {
    console.error('Error getting connected finance users:', error);
    return [];
  }
};

/**
 * Get requester's chat ID if connected
 */
const getRequesterChatId = async (userId: string): Promise<number | null> => {
  try {
    const { data, error } = await supabase
      .from('telegram_connections')
      .select('telegram_chat_id')
      .eq('user_id', userId)
      .eq('bot_type', 'payment')
      .eq('is_active', true)
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    return data.telegram_chat_id;
  } catch (error) {
    console.error('Error getting requester chat ID:', error);
    return null;
  }
};

/**
 * Send payment bot notification to specified chat IDs
 */
const sendPaymentBotNotification = async (
  chatIds: number[],
  message: string
): Promise<{ sent: number; failed: number }> => {
  if (chatIds.length === 0) {
    return { sent: 0, failed: 0 };
  }

  try {
    const { data, error } = await supabase.functions.invoke('telegram-payment-bot', {
      body: {
        action: 'send-notification',
        message,
        chatIds
      }
    });

    if (error) {
      console.error('Error sending payment notification:', error);
      return { sent: 0, failed: chatIds.length };
    }

    return {
      sent: data?.sent || 0,
      failed: data?.failed || 0
    };
  } catch (error) {
    console.error('Error sending payment notification:', error);
    return { sent: 0, failed: chatIds.length };
  }
};

/**
 * Notify approvers when a new payment request is submitted (with approval buttons)
 */
export const notifyApproversOfNewRequest = async (
  request: PaymentRequest
): Promise<void> => {
  try {
    if (!request.department_id) {
      console.log('No department ID, skipping approver notification');
      return;
    }

    const message = formatNewPaymentApprovalNotification(request);
    
    // Create inline keyboard with approve/reject buttons
    // Use short action codes (ap/rp). Full UUID is still under Telegram's 64-byte callback_data limit.
    const buttons = {
      inline_keyboard: [[
        { text: '✅ Approve', callback_data: `ap:${request.id}` },
        { text: '❌ Reject', callback_data: `rp:${request.id}` }
      ]]
    };

    // Call edge function to send approval request with buttons
    const { data, error } = await supabase.functions.invoke('telegram-payment-bot', {
      body: {
        action: 'send-approval-request',
        departmentId: request.department_id,
        message,
        buttons
      }
    });

    if (error) {
      console.error('Error sending approval request:', error);
      return;
    }
    
    console.log(`Payment approval notification: ${data?.sent || 0} sent, ${data?.failed || 0} failed`);
  } catch (error) {
    console.error('Error notifying approvers:', error);
  }
};

/**
 * Notify requester and finance when request is approved
 */
export const notifyPaymentApproved = async (
  request: PaymentRequest,
  approverName: string
): Promise<void> => {
  try {
    // Notify requester
    if (request.requestor_id) {
      const requesterChatId = await getRequesterChatId(request.requestor_id);
      if (requesterChatId) {
        const requesterMessage = formatPaymentApprovedNotification(request, approverName);
        await sendPaymentBotNotification([requesterChatId], requesterMessage);
      }
    }

    // Notify finance team with CONFIRM PAYMENT button
    const financeChatIds = await getConnectedFinanceUsers();
    if (financeChatIds.length > 0) {
      // Use the new edge function action that includes the Confirm Payment button
      await supabase.functions.invoke('telegram-payment-bot', {
        body: {
          action: 'send-finance-approval',
          request: {
            id: request.id,
            request_number: request.request_number,
            title: request.title,
            total_amount: request.total_amount,
            currency: request.currency || 'ETB',
            requester_name: getRequesterName(request),
            department_name: request.department?.name || 'N/A',
            vendor_name: request.vendor_name
          },
          approverName,
          chatIds: financeChatIds
        }
      });
    }

    console.log('Payment approved notifications sent');
  } catch (error) {
    console.error('Error notifying payment approved:', error);
  }
};

/**
 * Notify requester when request is rejected
 */
export const notifyPaymentRejected = async (
  request: PaymentRequest,
  reason: string,
  rejectorName?: string
): Promise<void> => {
  try {
    if (!request.requestor_id) {
      console.log('No requestor ID, skipping rejection notification');
      return;
    }

    const chatId = await getRequesterChatId(request.requestor_id);
    if (!chatId) {
      console.log('Requester not connected to payment bot');
      return;
    }

    const message = formatPaymentRejectedNotification(request, reason, rejectorName);
    const result = await sendPaymentBotNotification([chatId], message);
    
    console.log(`Payment rejection notification: ${result.sent} sent, ${result.failed} failed`);
  } catch (error) {
    console.error('Error notifying payment rejected:', error);
  }
};

/**
 * Notify requester when payment is confirmed
 */
export const notifyPaymentConfirmed = async (
  request: PaymentRequest,
  paymentReference?: string
): Promise<void> => {
  try {
    if (!request.requestor_id) {
      console.log('No requestor ID, skipping payment confirmation notification');
      return;
    }

    const chatId = await getRequesterChatId(request.requestor_id);
    if (!chatId) {
      console.log('Requester not connected to payment bot');
      return;
    }

    const message = formatPaymentConfirmedNotification(request, paymentReference);
    const result = await sendPaymentBotNotification([chatId], message);
    
    console.log(`Payment confirmation notification: ${result.sent} sent, ${result.failed} failed`);
  } catch (error) {
    console.error('Error notifying payment confirmed:', error);
  }
};

/**
 * Get payment bot connection for a user
 */
export const getUserPaymentBotConnection = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('telegram_connections')
      .select('*')
      .eq('user_id', userId)
      .eq('bot_type', 'payment')
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error getting payment bot connection:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error getting payment bot connection:', error);
    return null;
  }
};

/**
 * Get all payment bot connections
 */
export const getAllPaymentBotConnections = async () => {
  try {
    // First, get all payment bot connections (no join - no FK exists)
    const { data: connections, error: connectionsError } = await supabase
      .from('telegram_connections')
      .select('*')
      .eq('bot_type', 'payment')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (connectionsError) {
      console.error('Error getting payment bot connections:', connectionsError);
      return [];
    }

    if (!connections || connections.length === 0) {
      return [];
    }

    // Get unique user IDs and fetch their profiles separately
    const userIds = [...new Set(connections.map(c => c.user_id))];
    const { data: profiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select('id, first_name, last_name, role')
      .in('id', userIds);

    if (profilesError) {
      console.error('Error getting user profiles:', profilesError);
      // Return connections without profile data rather than failing
      return connections.map(c => ({ ...c, user_profiles: null }));
    }

    // Create a map for quick lookup
    const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

    // Merge connections with their profiles
    return connections.map(connection => ({
      ...connection,
      user_profiles: profileMap.get(connection.user_id) || null
    }));
  } catch (error) {
    console.error('Error getting payment bot connections:', error);
    return [];
  }
};

/**
 * Get payment bot statistics
 */
export const getPaymentBotStats = async () => {
  try {
    const { data, error } = await supabase
      .from('telegram_connections')
      .select('is_active')
      .eq('bot_type', 'payment');

    if (error) {
      console.error('Error getting payment bot stats:', error);
      return { total: 0, active: 0 };
    }

    const total = data?.length || 0;
    const active = data?.filter(c => c.is_active).length || 0;

    return { total, active };
  } catch (error) {
    console.error('Error getting payment bot stats:', error);
    return { total: 0, active: 0 };
  }
};

/**
 * Disconnect a user from payment bot
 */
export const disconnectPaymentBotUser = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('telegram_connections')
      .update({ is_active: false })
      .eq('user_id', userId)
      .eq('bot_type', 'payment');

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Generate connection code for payment bot
 */
export const generatePaymentBotConnectionCode = async (userId: string): Promise<{ code: string; expires_at: string } | null> => {
  try {
    // Generate a unique connection code
    const code = Math.random().toString(36).substring(2, 10).toUpperCase();
    
    // Set expiration time (24 hours from now)
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);
    
    // Delete existing unused codes for this user for payment bot
    await supabase
      .from('telegram_connection_codes')
      .delete()
      .eq('user_id', userId)
      .eq('bot_type', 'payment')
      .eq('used', false);
    
    // Insert the new code with payment bot type
    const { data, error } = await supabase
      .from('telegram_connection_codes')
      .insert({
        user_id: userId,
        code,
        expires_at: expiresAt.toISOString(),
        bot_type: 'payment'
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error generating connection code:', error);
      return null;
    }
    
    return {
      code,
      expires_at: expiresAt.toISOString()
    };
  } catch (error) {
    console.error('Failed to generate connection code:', error);
    return null;
  }
};
